/**
 * Visual Effects Controls Component
 *
 * Provides visual styling controls for landing pages:
 * - Border Radius: Corner rounding (0-32px)
 * - Box Shadow: Depth and elevation effects
 * - Background Gradient: Linear/radial gradients with color stops
 * - Opacity: Element transparency (0-100%)
 *
 * Features live preview for all effects.
 */

import { useState } from 'react';
import { Circle, Box, Sparkles, Minus, Plus, Sun, Trash2 } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { Button } from '@/client/components/ui/button';
import { ColorPicker } from '@/client/components/ui/color-picker';

// =============================================================================
// Types
// =============================================================================

export interface VisualEffectsSettings {
  // Border Radius
  borderRadius?: number; // in px, 0-32

  // Box Shadow
  shadowX?: number; // X offset, -20 to 20
  shadowY?: number; // Y offset, -20 to 20
  shadowBlur?: number; // Blur radius, 0-50
  shadowSpread?: number; // Spread radius, -20 to 20
  shadowColor?: string; // Shadow color
  shadowEnabled?: boolean; // Toggle shadow on/off

  // Gradient
  gradientEnabled?: boolean; // Use gradient vs solid color
  gradientType?: 'linear' | 'radial';
  gradientAngle?: number; // 0-360 degrees (for linear)
  gradientStops?: Array<{
    color: string;
    position: number; // 0-100%
  }>;

  // Opacity
  opacity?: number; // 0-100%
}

interface VisualEffectsControlsProps {
  value: VisualEffectsSettings;
  onChange: (settings: Partial<VisualEffectsSettings>) => void;
}

// =============================================================================
// Slider Component
// =============================================================================

function Slider({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  description,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (value: number) => void;
  description?: string;
}) {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="space-y-2 group">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{label}</label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => onChange(Math.max(min, value - step))}
            disabled={value <= min}
            className="h-6 w-6 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="text-sm font-mono min-w-[3.5rem] text-center text-muted-foreground group-hover:text-foreground group-focus-within:text-foreground transition-colors">
            {value.toFixed(step < 1 ? 1 : 0)}
            {unit}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => onChange(Math.min(max, value + step))}
            disabled={value >= max}
            className="h-6 w-6 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Slider Track */}
      <div
        className="relative h-2 bg-muted rounded-full cursor-pointer"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percentage = x / rect.width;
          const newValue = min + percentage * (max - min);
          onChange(Math.round(newValue / step) * step);
        }}
      >
        {/* Progress */}
        <div
          className="absolute left-0 top-0 h-full bg-primary rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
        {/* Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary border-2 border-background rounded-full shadow-md transition-all"
          style={{ left: `calc(${percentage}% - 0.5rem)` }}
        />
      </div>

      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}

// =============================================================================
// Visual Effects Controls Component
// =============================================================================

export function VisualEffectsControls({ value, onChange }: VisualEffectsControlsProps) {
  const [showGradientEditor, setShowGradientEditor] = useState(false);

  // Default values
  const borderRadius = value.borderRadius ?? 8;
  const shadowX = value.shadowX ?? 0;
  const shadowY = value.shadowY ?? 4;
  const shadowBlur = value.shadowBlur ?? 10;
  const shadowSpread = value.shadowSpread ?? 0;
  const shadowColor = value.shadowColor ?? '#00000040';
  const shadowEnabled = value.shadowEnabled ?? false;
  const gradientEnabled = value.gradientEnabled ?? false;
  const gradientType = value.gradientType ?? 'linear';
  const gradientAngle = value.gradientAngle ?? 135;
  const gradientStops = value.gradientStops ?? [
    { color: '#3b82f6', position: 0 },
    { color: '#8b5cf6', position: 100 },
  ];
  const opacity = value.opacity ?? 100;

  // Generate box shadow CSS value
  const generateBoxShadow = () => {
    if (!shadowEnabled) return 'none';
    return `${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowSpread}px ${shadowColor}`;
  };

  // Generate gradient CSS value
  const generateGradient = () => {
    if (!gradientEnabled) return undefined;

    const stops = gradientStops.map((stop) => `${stop.color} ${stop.position}%`).join(', ');

    if (gradientType === 'linear') {
      return `linear-gradient(${gradientAngle}deg, ${stops})`;
    } else {
      return `radial-gradient(circle, ${stops})`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Border Radius */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Circle className="h-4 w-4 text-muted-foreground" />
          <label className="text-sm font-medium">Border Radius</label>
        </div>

        <Slider
          label="Corner Rounding"
          value={borderRadius}
          min={0}
          max={32}
          step={1}
          unit="px"
          onChange={(v) => onChange({ borderRadius: v })}
          description="Roundness of corners"
        />
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Box Shadow */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Box className="h-4 w-4 text-muted-foreground" />
            <label className="text-sm font-medium">Box Shadow</label>
          </div>
          <Button
            type="button"
            variant={shadowEnabled ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => onChange({ shadowEnabled: !shadowEnabled })}
          >
            {shadowEnabled ? 'Enabled' : 'Disabled'}
          </Button>
        </div>

        {shadowEnabled && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Slider
                label="X Offset"
                value={shadowX}
                min={-20}
                max={20}
                step={1}
                unit="px"
                onChange={(v) => onChange({ shadowX: v })}
              />

              <Slider
                label="Y Offset"
                value={shadowY}
                min={-20}
                max={20}
                step={1}
                unit="px"
                onChange={(v) => onChange({ shadowY: v })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Slider
                label="Blur"
                value={shadowBlur}
                min={0}
                max={50}
                step={1}
                unit="px"
                onChange={(v) => onChange({ shadowBlur: v })}
              />

              <Slider
                label="Spread"
                value={shadowSpread}
                min={-20}
                max={20}
                step={1}
                unit="px"
                onChange={(v) => onChange({ shadowSpread: v })}
              />
            </div>

            <ColorPicker
              label="Shadow Color"
              value={shadowColor}
              onChange={(color) => onChange({ shadowColor: color })}
            />
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Background Gradient */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            <label className="text-sm font-medium">Background Gradient</label>
          </div>
          <Button
            type="button"
            variant={gradientEnabled ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => onChange({ gradientEnabled: !gradientEnabled })}
          >
            {gradientEnabled ? 'Enabled' : 'Disabled'}
          </Button>
        </div>

        {gradientEnabled && (
          <div className="space-y-4">
            {/* Gradient Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onChange({ gradientType: 'linear' })}
                  className={cn(
                    'px-3 py-2 rounded-lg border text-sm transition-colors',
                    gradientType === 'linear'
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  Linear
                </button>
                <button
                  type="button"
                  onClick={() => onChange({ gradientType: 'radial' })}
                  className={cn(
                    'px-3 py-2 rounded-lg border text-sm transition-colors',
                    gradientType === 'radial'
                      ? 'border-primary bg-primary/10 text-primary font-semibold'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  Radial
                </button>
              </div>
            </div>

            {/* Angle (for linear gradients) */}
            {gradientType === 'linear' && (
              <Slider
                label="Angle"
                value={gradientAngle}
                min={0}
                max={360}
                step={15}
                unit="°"
                onChange={(v) => onChange({ gradientAngle: v })}
                description="Direction of gradient"
              />
            )}

            {/* Color Stops */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Color Stops</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowGradientEditor(!showGradientEditor)}
                >
                  {showGradientEditor ? 'Hide' : 'Edit'} Stops
                </Button>
              </div>

              {showGradientEditor && (
                <div className="space-y-3">
                  {gradientStops.map((stop, index) => (
                    <div key={index} className="p-3 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Stop {index + 1}</span>
                        {gradientStops.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newStops = gradientStops.filter((_, i) => i !== index);
                              onChange({ gradientStops: newStops });
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>

                      <ColorPicker
                        label="Color"
                        value={stop.color}
                        onChange={(color) => {
                          const newStops = [...gradientStops];
                          newStops[index] = { ...stop, color };
                          onChange({ gradientStops: newStops });
                        }}
                      />

                      <Slider
                        label="Position"
                        value={stop.position}
                        min={0}
                        max={100}
                        step={5}
                        unit="%"
                        onChange={(position) => {
                          const newStops = [...gradientStops];
                          newStops[index] = { ...stop, position };
                          onChange({ gradientStops: newStops });
                        }}
                      />
                    </div>
                  ))}

                  {gradientStops.length < 5 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newStops = [
                          ...gradientStops,
                          {
                            color: '#ffffff',
                            position: 50,
                          },
                        ];
                        onChange({ gradientStops: newStops });
                      }}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Color Stop
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Opacity */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Sun className="h-4 w-4 text-muted-foreground" />
          <label className="text-sm font-medium">Opacity</label>
        </div>

        <Slider
          label="Transparency"
          value={opacity}
          min={0}
          max={100}
          step={5}
          unit="%"
          onChange={(v) => onChange({ opacity: v })}
          description="0% = fully transparent, 100% = fully opaque"
        />
      </div>

      {/* Live Preview */}
      <div className="p-4 border rounded-xl bg-muted/20">
        <p className="text-xs text-muted-foreground mb-3">Preview</p>
        <div className="flex justify-center">
          <div
            className="w-48 h-32 flex items-center justify-center text-sm font-medium"
            style={{
              borderRadius: `${borderRadius}px`,
              boxShadow: generateBoxShadow(),
              background: generateGradient() || '#3b82f6',
              opacity: opacity / 100,
            }}
          >
            <span className="text-white drop-shadow-md">Preview Box</span>
          </div>
        </div>
      </div>
    </div>
  );
}
